/**
 * API service for prompt template management
 */

import { apiClient } from './index';

export interface PromptType {
  type: string;
  display_name: string;
  description: string;
  default_variables: string[];
}

export interface PersonaInfo {
  name: string;
  display_name: string;
  description: string;
  is_custom: boolean;
}

export interface PromptTemplate {
  id: string;
  prompt_type: string;
  persona_name: string;
  organization_id: string | null;
  user_id: string | null;
  prompt_template: string;
  display_name: string;
  description: string | null;
  temperature: number;
  required_variables: string[];
  example_output: string | null;
  is_active: boolean;
  is_default: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface UserPromptPreference {
  id: string;
  user_id: string;
  organization_id: string;
  prompt_type: string;
  preferred_persona: string;
  created_at: string;
  updated_at: string;
}

export interface PromptTypesResponse {
  types: PromptType[];
}

export interface PersonasForTypeResponse {
  prompt_type: string;
  personas: PersonaInfo[];
  user_preference: string | null;
}

export interface PromptTemplateListResponse {
  templates: PromptTemplate[];
  total: number;
}

export interface PromptTemplatePreviewRequest {
  prompt_template: string;
  variables: Record<string, string>;
}

export interface PromptTemplatePreviewResponse {
  rendered_prompt: string;
  missing_variables: string[];
  warnings: string[];
}

export interface UserPromptPreferencesResponse {
  preferences: UserPromptPreference[];
}

export interface UserPreferenceSet {
  prompt_type: string;
  preferred_persona: string;
}

export interface PromptTemplateCreate {
  prompt_type: string;
  persona_name: string;
  prompt_template: string;
  display_name: string;
  temperature: number;
  required_variables: string[];
  description?: string;
  example_output?: string;
  is_default?: boolean;
}

export interface PromptTemplateUpdate {
  prompt_template?: string;
  display_name?: string;
  description?: string;
  temperature?: number;
  required_variables?: string[];
  example_output?: string;
  is_default?: boolean;
  is_active?: boolean;
}

// Get all available prompt types
export const getPromptTypes = async (): Promise<PromptTypesResponse> => {
  const response = await apiClient.get<PromptTypesResponse>('/prompt-templates/types');
  return response.data;
};

// Get available personas for a specific prompt type
export const getPersonasForType = async (
  promptType: string
): Promise<PersonasForTypeResponse> => {
  const response = await apiClient.get<PersonasForTypeResponse>(`/prompt-templates/types/${promptType}/personas`);
  return response.data;
};

// List all prompt templates visible to the user
export const listPromptTemplates = async (
  promptType?: string,
  includeSystem?: boolean
): Promise<PromptTemplateListResponse> => {
  const params: Record<string, string | boolean> = {};
  if (promptType) params.prompt_type = promptType;
  if (includeSystem !== undefined) params.include_system = includeSystem;

  const response = await apiClient.get<PromptTemplateListResponse>('/prompt-templates', { params });
  return response.data;
};

// Get a specific prompt template
export const getPromptTemplate = async (templateId: string): Promise<PromptTemplate> => {
  const response = await apiClient.get<PromptTemplate>(`/prompt-templates/${templateId}`);
  return response.data;
};

// Create a user-level prompt template
export const createUserPromptTemplate = async (
  data: PromptTemplateCreate
): Promise<PromptTemplate> => {
  const response = await apiClient.post<PromptTemplate>('/prompt-templates/user', data);
  return response.data;
};

// Update a prompt template
export const updatePromptTemplate = async (
  templateId: string,
  data: PromptTemplateUpdate
): Promise<PromptTemplate> => {
  const response = await apiClient.put<PromptTemplate>(`/prompt-templates/${templateId}`, data);
  return response.data;
};

// Delete a prompt template
export const deletePromptTemplate = async (templateId: string): Promise<void> => {
  await apiClient.delete(`/prompt-templates/${templateId}`);
};

// Preview a prompt template with example variables
export const previewPromptTemplate = async (
  data: PromptTemplatePreviewRequest
): Promise<PromptTemplatePreviewResponse> => {
  const response = await apiClient.post<PromptTemplatePreviewResponse>('/prompt-templates/preview', data);
  return response.data;
};

// Get user's prompt preferences
export const getUserPreferences = async (): Promise<UserPromptPreferencesResponse> => {
  const response = await apiClient.get<UserPromptPreferencesResponse>('/prompt-templates/preferences/user');
  return response.data;
};

// Set user's preferred persona for a prompt type
export const setUserPreference = async (
  data: UserPreferenceSet
): Promise<UserPromptPreference> => {
  const response = await apiClient.put<UserPromptPreference>('/prompt-templates/preferences/user', data);
  return response.data;
};

// Delete user's preference for a prompt type
export const deleteUserPreference = async (promptType: string): Promise<void> => {
  await apiClient.delete(`/prompt-templates/preferences/user/${promptType}`);
};
